import React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import ReportStore from '../DataRepository/Reports';
import { reportsModel } from '../Axios/AxiosConfig';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import {
    IconButton, CircularProgress, TextField, Button, Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Select, MenuItem, FormControl
} from '@mui/material';

import Accordion from '@mui/material/Accordion';
import { CheckCircle, ArrowUpward, Edit } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionActions from '@mui/material/AccordionActions';
import { Circle, AlertTriangle } from 'lucide-react';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import ChevronLeft from '@mui/icons-material/ChevronLeft';

function ViewReportList() {
    const { Reports, isReportsLoading, getReports } = ReportStore()

    const { UID, id } = useParams()
    const selectedReport = Reports.filter((e) => e.id === id)?.[0]

    const navigate = useNavigate()

    const [previewUrl, setPreviewUrl] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const handlePreview = async (url) => {
        setIsPreviewOpen(true);
        setPreviewUrl(null);
        try {
            const res = await fetch(url, { mode: 'cors' });
            if (!res.ok) throw new Error("Fetch failed");
            const blob = await res.blob();
            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
            const bUrl = URL.createObjectURL(pdfBlob);
            setPreviewUrl(bUrl);
        } catch (e) {
            console.error("Preview fetch failed, using fallback:", e);
            setPreviewUrl(`https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`);
        }
    };

    const closePreview = () => {
        if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }
        setIsPreviewOpen(false);
        setPreviewUrl(null);
    };

    useEffect(() => {
        getReports(UID)
        window.scrollTo(0, 0);
    }, [UID, getReports]);

    useEffect(() => {
        if (!isReportsLoading && selectedReport?.Reports?.length === 1) {
            navigate(`/ViewReport/${UID}/${id}/0`, { replace: true });
        }
    }, [selectedReport, isReportsLoading, UID, id, navigate]);

    const [editMode, setEditMode] = useState(false);
    const [editableReports, setEditableReports] = useState([]);

    const toggleEdit = () => setEditMode(prev => !prev);



    useEffect(() => {
        if (selectedReport?.Reports?.length) {
            const parsedReports = selectedReport.Reports.map((report) => {
                try {
                    return JSON.parse(report?.ai_analysis || '{}');
                } catch {
                    return {};
                }
            });
            setEditableReports(parsedReports);
        }
    }, [selectedReport]);


    const handleParamChange = (reportIndex, type, paramIndex, field, value) => {
        const updated = [...editableReports];
        const currentReport = { ...updated[reportIndex] };
        const targetList = [...(currentReport[type] || [])];
        const updatedParam = { ...targetList[paramIndex], [field]: value };

        if (field === 'status') {
            const isNormalStatus = value.toLowerCase().includes('normal');
            const currentlyInNormal = type === 'normal_parameters';

            // Move from abnormal → normal
            if (isNormalStatus && !currentlyInNormal) {
                const abnormalList = [...(currentReport.abnormal_parameters || [])];
                // remove param safely by id (or reference) instead of index
                const filteredAbnormal = abnormalList.filter((_, i) => i !== paramIndex);

                currentReport.abnormal_parameters = filteredAbnormal;
                currentReport.normal_parameters = [
                    ...(currentReport.normal_parameters || []),
                    updatedParam
                ];
            }

            // Move from normal → abnormal
            else if (!isNormalStatus && currentlyInNormal) {
                const normalList = [...(currentReport.normal_parameters || [])];
                const filteredNormal = normalList.filter((_, i) => i !== paramIndex);

                currentReport.normal_parameters = filteredNormal;
                currentReport.abnormal_parameters = [
                    ...(currentReport.abnormal_parameters || []),
                    updatedParam
                ];
            } else {
                // Just update in place if no movement between lists
                targetList[paramIndex] = updatedParam;
                currentReport[type] = targetList;
            }

            updated[reportIndex] = currentReport;
            setEditableReports(updated);
            return;
        }

        targetList[paramIndex] = updatedParam;
        currentReport[type] = targetList;
        updated[reportIndex] = currentReport;
        setEditableReports(updated);
    };

    const handleSaveAll = async () => {
        try {
            const updated = selectedReport.Reports.map((r, i) => ({
                ...r,
                ai_analysis: JSON.stringify(editableReports[i] || {})
            }));

            const res = await reportsModel.post('/editAnalyzedReport', {
                id,
                editableReports: updated
            });

            if (res.status === 200) setEditMode(false);
        } catch (error) {
            console.error("Failed to save all reports", error);
        }
    };

    const getStatusTag = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'low':
                return <span className="px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800 border border-yellow-300">Low</span>;
            case 'high':
                return <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-800 border border-red-300">High</span>;
            case 'normal':
                return <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-800 border border-green-300">Normal</span>;
            case 'borderline':
                return <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-800 border border-orange-300">Borderline</span>;
            case 'borderline_low':
                return <span className="px-2 py-0.5 text-xs rounded bg-yellow-200 text-yellow-900 border border-yellow-400">Borderline Low</span>;
            case 'borderline_high':
                return <span className="px-2 py-0.5 text-xs rounded bg-orange-200 text-orange-900 border border-orange-400">Borderline High</span>;
            case 'critical':
                return <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800 border border-purple-300">Critical</span>;
            case 'missed':
                return <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-600 border border-gray-300">Missed</span>;
            default:
                return <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700 border border-gray-200">Unknown</span>;
        }
    };

    const getIcon = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'low':
            case 'high':
                return <AlertTriangle className="text-red-500 w-5 h-5 mr-2" />;
            case 'normal':
                return <CheckCircle className="text-green-500 w-5 h-5 mr-2" />;
        }
    };



    return (
        <div className="All-products-section-main">
            <nav className="flex flex-row p-2 items-center justify-between">
                <div className="flex flex-row items-center">
                    <IconButton onClick={() => navigate(`/Reports/${UID}`)}>
                        <ChevronLeft />
                    </IconButton>
                    <p className="mb-0">Reports</p>
                </div>
            </nav>

            <div className="Reports-main-page px-4 py-2">
                <Typography variant="h6" sx={{ fontFamily: "'Poppins', sans-serif", mb: 2 }}>
                    {selectedReport?.ReportType}
                </Typography>

                <div className="flex gap-6">

                    <div style={{ flex: 2 }}>
                        {/* <div className="flex items-center justify-end mb-2">
                            <Button
                                variant="contained"
                                style={{ backgroundColor: "#DA6E0B" }}
                                onClick={editMode ? handleSaveAll : toggleEdit}
                            >
                                {editMode ? "Save All" : "Edit All"}
                            </Button>
                        </div> */}

                        <div className="di-selectedReportscards grid grid-cols-2 gap-4">
                            {selectedReport?.Reports.map((e, i) => (
                                <div
                                    className="SelectedReportCard"
                                    key={i}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate(`/ViewReport/${UID}/${id}/${i}`)}
                                >
                                    {selectedReport.filetype === "images" ? (
                                        <img src={e?.Location} alt="Image" />
                                    ) : (
                                        <img src="/icons/pdf icon.png" alt="PDF" />
                                    )}
                                    <p className="text-center mt-2">{e?.Key?.split("/")[3]}</p>
                                </div>
                            ))}
                        </div>
                    </div>


                    <div style={{ flex: 3, overflowY: "auto", maxHeight: "80vh" }}>
                        {selectedReport?.Reports.map((report, i) => {
                            const parsed = editableReports[i];

                            if (!parsed || parsed === "AI analysis failed") return null;

                            const types = ["abnormal_parameters", "normal_parameters"];

                            return (
                                <div key={i} className="mb-6 p-3 bg-white shadow rounded">
                                    <div className="flex justify-between items-center mb-2">
                                        <Typography variant="h6" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                            Report #{i + 1}
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            style={{ color: "#DA6E0B", borderColor: "#DA6E0B", textTransform: "none" }}
                                            onClick={() => handlePreview(report.Location)}
                                        >
                                            View PDF
                                        </Button>
                                    </div>

                                    {editMode ? (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>Name</strong></TableCell>
                                                    <TableCell><strong>Value</strong></TableCell>
                                                    <TableCell><strong>Status</strong></TableCell>
                                                    <TableCell><strong>Reference Range</strong></TableCell>
                                                    <TableCell><strong>Note</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {types.flatMap((type) =>
                                                    (parsed?.[type] || []).map((param, j) => (
                                                        <TableRow key={`${i}-${type}-${j}`}>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    value={param.name || ""}
                                                                    onChange={(e) => handleParamChange(i, type, j, "name", e.target.value)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    value={param.value || ""}
                                                                    onChange={(e) => handleParamChange(i, type, j, "value", e.target.value)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <FormControl size="small" fullWidth>
                                                                    <Select
                                                                        value={param.status || ""}
                                                                        onChange={(e) => handleParamChange(i, type, j, "status", e.target.value)}
                                                                    >
                                                                        <MenuItem value="normal">Normal</MenuItem>
                                                                        <MenuItem value="low">Low</MenuItem>
                                                                        <MenuItem value="high">High</MenuItem>
                                                                        <MenuItem value="borderline">Borderline</MenuItem>
                                                                        <MenuItem value="borderline_low">Borderline Low</MenuItem>
                                                                        <MenuItem value="borderline_high">Borderline High</MenuItem>
                                                                        <MenuItem value="critical">Critical</MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    value={param.reference_range || ""}
                                                                    onChange={(e) => handleParamChange(i, type, j, "reference_range", e.target.value)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    value={param.reason || ""}
                                                                    onChange={(e) => handleParamChange(i, type, j, "reason", e.target.value)}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        types.flatMap((type) =>
                                            (parsed?.[type] || []).map((param, j) => {
                                                const status = (param.status || "").toLowerCase();
                                                const backgroundColor =
                                                    status === "low" || status === "high"
                                                        ? "#FFF5F5"
                                                        : status === "normal"
                                                            ? "#F1EFEC"
                                                            : "#F5F5F5";

                                                return (
                                                    <Accordion key={`${i}-${type}-${j}`} sx={{ backgroundColor, mb: 1 }}>
                                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                            <Box className="flex items-center gap-2 w-full" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                                                {getIcon(status)}
                                                                <Box className="flex items-center justify-between w-full">
                                                                    <Box className="flex items-center gap-2">
                                                                        <span>{param.name}</span>
                                                                        <Typography variant="body2">({param.value})</Typography>
                                                                    </Box>
                                                                    {getStatusTag(status)}
                                                                </Box>
                                                            </Box>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <Typography variant="body2" sx={{ color: "#333" }}>
                                                                <strong>Reference Range:</strong> {param.reference_range}
                                                            </Typography>
                                                            {param.reason && (
                                                                <Typography variant="body2" sx={{ color: "#555", mt: 1 }}>
                                                                    <strong>Note:</strong> {param.reason}
                                                                </Typography>
                                                            )}
                                                        </AccordionDetails>
                                                    </Accordion>
                                                );
                                            })
                                        )
                                    )}
                                </div>
                            );
                        })}
                    </div>

                </div>
            </div>

            <Dialog
                open={isPreviewOpen}
                onClose={closePreview}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { height: '90vh', borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Report Preview</Typography>
                    <IconButton onClick={closePreview}>
                        <ChevronLeft sx={{ transform: 'rotate(90deg)' }} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: 'hidden', height: '100%' }}>
                    {previewUrl ? (
                        <iframe
                            src={`${previewUrl}#toolbar=0`}
                            width="100%"
                            height="100%"
                            title="PDF Preview"
                            style={{ border: "none" }}
                        ></iframe>
                    ) : (
                        <Box className="flex items-center justify-center h-full">
                            <CircularProgress sx={{ color: "#DA6E0B" }} />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );


}

export default ViewReportList